import { ApiClientError, NetworkError } from '@recipes/api-client';
import type { VersionConflictDetails } from '@recipes/contracts';
import i18n from './i18n';
import { toast } from '@/components/feedback/toaster';

/** Localized, user-facing message for any error thrown by the API client. */
export function describeError(error: unknown): string {
  if (error instanceof ApiClientError) {
    // The server says why (`reason` + `params`), the client chooses the words. The English
    // `message` is for logs – it is only shown when this build knows neither reason nor code.
    const { reason, params } = error.error;
    if (reason && i18n.exists(`errors.reasons.${reason}`))
      return i18n.t(`errors.reasons.${reason}`, params ?? {});
    const key = `errors.${error.code}`;
    return i18n.exists(key) ? i18n.t(key) : error.message;
  }
  if (error instanceof NetworkError) return i18n.t('errors.NETWORK');
  if (error instanceof Error) return error.message;
  return i18n.t('errors.INTERNAL_ERROR');
}

/**
 * A failure that happened in the worker (AI generation, transcription): translated by its code,
 * with the stored English message as the last resort for codes this build does not know.
 */
export function describeWorkerError(
  code: string | null | undefined,
  message?: string | null,
): string {
  if (code && i18n.exists(`errors.worker.${code}`)) return i18n.t(`errors.worker.${code}`);
  return message || i18n.t('errors.worker.GENERATION_FAILED');
}

export function isVersionConflict(
  error: unknown,
): error is ApiClientError & { error: { details: VersionConflictDetails } } {
  return error instanceof ApiClientError && error.code === 'VERSION_CONFLICT';
}

export function isNotFound(error: unknown): boolean {
  return error instanceof ApiClientError && error.status === 404;
}

/** Field-level issues from a VALIDATION_ERROR envelope, keyed by dotted path. */
export function validationIssues(error: unknown): Record<string, string> {
  if (!(error instanceof ApiClientError) || error.code !== 'VALIDATION_ERROR') return {};
  const details = error.error.details as Array<{ path: string; message: string }> | undefined;
  if (!Array.isArray(details)) return {};
  return Object.fromEntries(details.map((d) => [d.path, d.message]));
}

export function toastError(error: unknown, title?: string): void {
  toast.error(title ?? i18n.t('app.unexpectedError'), describeError(error));
}
